import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import {
  formatExperimentTitle,
  getLabRemoteConfigPayload,
  getLabRemoteConfigStatusMessage,
  hasLabPayload,
} from './ExperimentPanelHelpers'
import LabPayloadLinkIcon from './LabPayloadLinkIcon'

interface LabRemoteFlagItemProps {
  flagKey: string
  remoteConfig?: App.LabRemoteConfigResult
}

function LabRemoteFlagItem({ flagKey, remoteConfig }: LabRemoteFlagItemProps) {
  const intl = useIntl()
  const title = useMemo(() => formatExperimentTitle(flagKey), [flagKey])
  const payload = useMemo(() => getLabRemoteConfigPayload(remoteConfig), [remoteConfig])
  const statusMessage = useMemo(() => getLabRemoteConfigStatusMessage(remoteConfig), [remoteConfig])
  const remoteFlagTypeLabel = intl.formatMessage({
    id: 'temp_lab_remote_flag_item_type_label',
    defaultMessage: 'Lab Remote Flag',
    description: 'Type label shown for a Lab remote config flag entry in the internal AB control panel',
  })
  const payloadLabel = intl.formatMessage({
    id: 'temp_lab_remote_flag_item_payload_label',
    defaultMessage: 'Payload',
    description: 'Read-only label beside the Lab remote config payload link in the internal AB control panel',
  })

  return (
    <Group direction="vertical" gap={20}>
      <BodyText variant="large">
        {title}
      </BodyText>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <BodyText variant="medium" colour="light-foreground-alt">
          <i>{remoteFlagTypeLabel}</i>
        </BodyText>
        {statusMessage ? (
          <BodyText variant="small" colour="light-foreground-alt">
            {statusMessage}
          </BodyText>
        ) : hasLabPayload(payload) && (
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <BodyText variant="small" colour="light-foreground-alt">
              {payloadLabel}
            </BodyText>
            <LabPayloadLinkIcon flagKey={flagKey} payload={payload} />
          </Group>
        )}
      </Group>
    </Group>
  )
}

export default LabRemoteFlagItem
